import React from "react";
import { FaUserGraduate } from "react-icons/fa";

import SectionCard from "./SectionCard";

import {
  formatDate,
  getAdherentCreatedDate,
  getAdherentDisplayName,
  getInitials,
} from "../utils/dashboardUtils";

const getGroupBadgeClass = (groupColorMap, groupName) => {
  const colorClass = groupColorMap?.[groupName]?.className;

  if (!colorClass || colorClass === "bg-white") {
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
  }

  if (colorClass === "bg-black") {
    return "bg-slate-950 text-white dark:bg-white dark:text-slate-950";
  }

  return `${colorClass} text-white`;
};

function RecentAdherentSkeleton() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
      <div className="mx-auto h-16 w-16 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      <div className="mx-auto mt-4 h-4 w-28 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
      <div className="mx-auto mt-3 h-3 w-20 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

export default function RecentAdherentsGrid({
  loading,
  adherents = [],
  groupColorMap = {},
}) {
  return (
    <SectionCard
      icon={FaUserGraduate}
      title="Derniers adhérents inscrits"
      subtitle="Les derniers membres ajoutés dans la base du club."
    >
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <RecentAdherentSkeleton key={index} />
          ))}
        </div>
      ) : adherents.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {adherents.map((adherent) => {
            const fullName = getAdherentDisplayName(adherent);
            const groupName = adherent?.adherent_groupe || "Sans groupe";
            const createdAt = getAdherentCreatedDate(adherent);

            return (
              <article
                key={adherent.id}
                className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-4 text-center transition hover:-translate-y-1 hover:border-orange-200 hover:bg-white hover:shadow-lg hover:shadow-slate-200/80 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-orange-500/30 dark:hover:bg-slate-900 dark:hover:shadow-none"
              >
                <div className="absolute right-4 top-4">
                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${getGroupBadgeClass(
                      groupColorMap,
                      groupName
                    )}`}
                  >
                    {groupName}
                  </span>
                </div>

                <div className="mx-auto grid h-20 w-20 place-items-center rounded-3xl bg-gradient-to-br from-orange-100 to-blue-100 text-xl font-bold text-slate-800 ring-4 ring-white dark:from-orange-500/20 dark:to-blue-500/20 dark:text-white dark:ring-slate-900">
                  {getInitials(adherent?.prenom, adherent?.nom)}
                </div>

                <h3 className="mt-4 truncate text-sm font-bold text-slate-950 dark:text-white">
                  {fullName}
                </h3>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Inscrit le {formatDate(createdAt)}
                </p>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950">
          <p className="font-bold text-slate-800 dark:text-slate-100">
            Aucun adhérent récent.
          </p>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Les nouvelles inscriptions apparaîtront ici automatiquement.
          </p>
        </div>
      )}
    </SectionCard>
  );
}